import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Link } from 'react-router-dom';
import { AdSenseUnit } from '@/components/marketplace/AdSenseUnit';
import { getVisitorId } from '@/lib/visitor';

/**
 * SponsoredAd — renders one ad tile.
 * Fires a deduped impression on mount and a click event on link click.
 */
export const SponsoredAd = ({ ad, className = '', variant = 'banner' }) => {
  useEffect(() => {
    if (!ad?.id) return;
    api.post(`/sponsored-ads/${ad.id}/impression`, null, { params: { vid: getVisitorId() } }).catch(() => {});
  }, [ad?.id]);

  const onClick = () => {
    api.post(`/sponsored-ads/${ad.id}/click`, null, { params: { vid: getVisitorId() } }).catch(() => {});
  };

  if (!ad) return null;
  const isExternal = /^https?:\/\//i.test(ad.click_url);

  // Card variant — matches the surrounding blog/listing cards (16:9 cover +
  // text below, fills the grid cell height). Banner variant is the original
  // wide 21:6 hero used in full-width placements.
  const inner = variant === 'card' ? (
    <div className='relative rounded-2xl overflow-hidden border bg-background h-full flex flex-col group'>
      <div className='aspect-[16/9] bg-secondary overflow-hidden relative'>
        <img
          src={ad.image_url}
          alt={ad.title}
          className='w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500'
          loading='lazy'
        />
        <span className='absolute top-2 right-2 text-[10px] font-medium uppercase rounded bg-black/60 text-white px-1.5 py-0.5' data-testid='sponsored-tag'>
          Sponsored
        </span>
      </div>
      <div className='p-5 flex-1 flex flex-col'>
        <h3 className='font-semibold text-lg leading-snug group-hover:underline underline-offset-4'>{ad.title}</h3>
        {ad.subtitle && <p className='text-sm text-muted-foreground mt-2 line-clamp-3'>{ad.subtitle}</p>}
        <div className='mt-auto pt-4 text-xs text-muted-foreground'>
          Sponsored{ad.advertiser_name ? ` · ${ad.advertiser_name}` : ''}
        </div>
      </div>
    </div>
  ) : (
    <div className='relative rounded-2xl overflow-hidden border bg-background group'>
      <div className='h-36 sm:h-40 lg:h-44 bg-secondary relative overflow-hidden'>
        <img
          src={ad.image_url}
          alt={ad.title}
          className='w-full h-full object-cover group-hover:scale-105 transition-transform duration-500'
          loading='lazy'
        />
        <div className='absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent' />
        <div className='absolute inset-0 p-5 sm:p-6 flex flex-col justify-center max-w-2xl'>
          <div className='text-white text-lg sm:text-2xl font-semibold leading-snug'>{ad.title}</div>
          {ad.subtitle && <div className='text-white/85 text-sm mt-1'>{ad.subtitle}</div>}
          {ad.advertiser_name && (
            <div className='text-white/70 text-[11px] mt-2'>Sponsored · {ad.advertiser_name}</div>
          )}
        </div>
        <span className='absolute top-2 right-2 text-[10px] font-medium uppercase rounded bg-black/60 text-white px-1.5 py-0.5' data-testid='sponsored-tag'>
          Sponsored
        </span>
      </div>
    </div>
  );

  const wrapClass = `${variant === 'card' ? 'block h-full' : ''} ${className}`.trim();

  return isExternal ? (
    <a href={ad.click_url} onClick={onClick} target='_blank' rel='noreferrer sponsored' className={wrapClass} data-testid={`sponsored-ad-${ad.placement}`}>
      {inner}
    </a>
  ) : (
    <Link to={ad.click_url || '#'} onClick={onClick} className={wrapClass} data-testid={`sponsored-ad-${ad.placement}`}>
      {inner}
    </Link>
  );
};

/**
 * SponsoredAdsRow — fetches ads for a placement and renders them stacked.
 */
export const SponsoredAdsRow = ({ placement, listingType = null, limit = 2, className = '', variant = 'banner' }) => {
  const [ads, setAds] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [adStatus, setAdStatus] = useState(null); // null=pending, 'filled'|'unfilled'|'disabled'
  useEffect(() => {
    const params = { placement, limit };
    if (listingType) params.listing_type = listingType;
    api.get('/sponsored-ads/public', { params })
      .then(r => setAds(r.data || []))
      .catch(() => setAds([]))
      .finally(() => setLoaded(true));
  }, [placement, listingType, limit]);
  if (!loaded) return null;
  // House ads take priority; when none are booked for this placement, backfill
  // with AdSense. If AdSense is disabled or has no ad to serve (unfilled), the
  // slot collapses entirely so no blank space is left behind.
  if (ads.length === 0) {
    if (adStatus === 'unfilled' || adStatus === 'disabled') return null;
    // Consistent ad container height so a future Google/house ad can't blow up
    // the layout (mirrors the banner variant's capped height).
    const fallbackH = variant === 'banner' ? 'max-h-44 overflow-hidden flex items-center justify-center' : '';
    return <div className={`${className} ${fallbackH}`.trim()}><AdSenseUnit onResolved={setAdStatus} /></div>;
  }
  return (
    <div className={`space-y-3 ${className}`}>
      {ads.map(a => <SponsoredAd key={a.id} ad={a} variant={variant} />)}
    </div>
  );
};

export default SponsoredAd;
